<template>
    <branded-split-screen
        heading="Confirm your payment"
        description="Your bank wants a second check on this payment. Confirm it with them and you're done."
    >
        <div>
            <div v-if="loading" class="flex justify-center">
                <img
                    src="https://cdn.zenmanage.com/images/spinner-1s-200px-taupe-500.png"
                    alt="[Spinner]"
                    class="h-8 w-8"
                />
            </div>

            <div
                v-else-if="loadError"
                class="space-y-4"
                data-testid="payment-authentication-load-error"
            >
                <div class="text-xl">{{ loadError.title }}</div>
                <p class="text-taupe-600 dark:text-taupe-300">
                    {{ loadError.detail }}
                </p>
            </div>

            <div
                v-else-if="state === 'requires_action'"
                class="space-y-6"
                data-testid="payment-authentication-confirm"
            >
                <h2
                    class="text-2xl font-semibold text-taupe-900 dark:text-taupe-50"
                >
                    Invoice {{ invoiceNumber }}
                </h2>

                <p class="text-taupe-600 dark:text-taupe-300">
                    {{ accountName }} owes {{ amountLabel }} on invoice
                    {{ invoiceNumber }}. Your bank asked for a check before it
                    lets the payment through.
                </p>

                <zen-message-block v-if="message" type="error">
                    {{ message }}
                </zen-message-block>

                <template v-if="paymentMethodId">
                    <zen-button
                        variant="primary"
                        size="large"
                        full-width
                        :processing="confirming"
                        @click="confirm"
                    >
                        {{ message ? 'Try again' : 'Confirm payment' }}
                    </zen-button>

                    <p class="text-sm text-taupe-500 dark:text-taupe-400">
                        Your bank opens its own check, in this window or a
                        pop-up. We never see your card details or your bank
                        login.
                    </p>
                </template>

                <p v-else class="text-taupe-600 dark:text-taupe-300">
                    We don't have a card on file for this account any more. Sign
                    in and add one on your
                    <router-link
                        :to="billingUrl"
                        class="text-sky-600 dark:text-sky-400 hover:underline"
                        >billing page</router-link
                    >
                    and we'll take payment with it.
                </p>
            </div>

            <div
                v-else-if="state === 'paid'"
                class="space-y-4"
                data-testid="payment-authentication-paid"
            >
                <div class="text-xl">Payment confirmed</div>
                <p class="text-taupe-600 dark:text-taupe-300">
                    We took {{ amountLabel }} for invoice
                    {{ invoiceNumber }} from {{ accountName }}. A receipt is on
                    its way to your billing email.
                </p>
            </div>

            <div
                v-else-if="state === 'processing'"
                class="space-y-4"
                data-testid="payment-authentication-processing"
            >
                <div class="text-xl">Waiting on your bank</div>
                <p class="text-taupe-600 dark:text-taupe-300">
                    Your bank hasn't confirmed this payment yet. You don't need
                    to do anything, and you shouldn't pay again. We'll email you
                    as soon as it settles.
                </p>
            </div>

            <div
                v-else
                class="space-y-4"
                data-testid="payment-authentication-failed"
            >
                <div class="text-xl">This payment can't be confirmed here</div>
                <p class="text-taupe-600 dark:text-taupe-300">
                    It was declined, replaced by a newer attempt, or the time to
                    confirm it ran out. If invoice {{ invoiceNumber }} is still
                    unpaid, we'll email you what happens next. You can also
                    switch to a different card on your
                    <router-link
                        :to="billingUrl"
                        class="text-sky-600 dark:text-sky-400 hover:underline"
                        >billing page</router-link
                    >.
                </p>
            </div>
        </div>
    </branded-split-screen>
</template>

<script>
import { markRaw } from 'vue'

import BrandedSplitScreen from '@/screens/_components/BrandedSplitScreen.vue'
import PaymentAuthenticationService from '@/services/payment-authentication.service'
import { currency } from '@/utils/formatters'

export default {
    components: {
        BrandedSplitScreen,
    },
    data() {
        return {
            loading: true,
            loadError: null,
            confirming: false,
            message: '',
            state: null,
            accountName: '',
            invoiceNumber: '',
            amount: 0,
            clientSecret: null,
            paymentMethodId: null,
            stripePublicKey: null,
            stripe: null,
        }
    },
    computed: {
        paymentUlid() {
            return this.$route.params.paymentUlid
        },
        signature() {
            return this.$route.query.signature
        },
        amountLabel() {
            return currency(this.amount)
        },
        billingUrl() {
            return '/admin/billing'
        },
    },
    methods: {
        /**
         * Take what the API says about the payment. A response that is not
         * offering the payment any more (no secret) leaves the secret the page
         * already holds alone, so a customer who closed their bank's check can
         * try again without a reload.
         */
        apply(data) {
            this.state = data.state
            this.accountName = data.account_name
            this.invoiceNumber = data.invoice_number
            this.amount = data.amount
            this.stripePublicKey =
                data.stripe_public_key ?? this.stripePublicKey

            if (data.client_secret) {
                this.clientSecret = data.client_secret
                this.paymentMethodId = data.payment_method_id
            }
        },
        describeLoadError(error) {
            const status = error?.response?.status

            // The link itself is refused: a signature that does not match, or
            // a payment that is not there.
            if (status === 403 || status === 404) {
                return {
                    title: 'This link is no longer valid',
                    detail: 'It may have been copied wrong. Open the latest email we sent about this payment, or sign in and check your billing page.',
                }
            }

            if (status === 503) {
                return {
                    title: "We couldn't reach our payment provider",
                    detail: 'Give it a minute and reload this page. Nothing was charged.',
                }
            }

            // No answer at all (a dropped connection) or any other failure on
            // our side says nothing about the link, so it is not blamed.
            return {
                title: "We couldn't load this payment",
                detail: 'Check your connection and reload this page. Nothing was charged.',
            }
        },
        /**
         * One Stripe.js client for the page, made the first time it is needed:
         * "Try again" reuses it rather than starting another. Kept out of
         * reactivity, since Stripe's objects are not plain data.
         */
        stripeClient() {
            if (!this.stripe) {
                this.stripe = markRaw(window.Stripe(this.stripePublicKey))
            }

            return this.stripe
        },
        /**
         * Puts the bank's check in front of the customer. An off-session charge
         * the bank refused to wave through is confirmed again with the saved
         * card, now that the customer is here; one a check is already open on
         * carries on from where it stopped.
         */
        async confirm() {
            this.confirming = true
            this.message = ''

            try {
                const stripe = this.stripeClient()
                const { paymentIntent } = await stripe.retrievePaymentIntent(
                    this.clientSecret
                )

                const result =
                    paymentIntent?.status === 'requires_action'
                        ? await stripe.handleNextAction({
                              clientSecret: this.clientSecret,
                          })
                        : await stripe.confirmCardPayment(this.clientSecret, {
                              payment_method: this.paymentMethodId,
                          })

                if (result.error) {
                    this.message = result.error.message
                }

                // Whatever the bank said, the API brings the payment up to date
                // and says what it is now: paid, failed, or still open.
                this.apply(
                    await PaymentAuthenticationService.complete(
                        this.paymentUlid,
                        this.signature
                    )
                )

                if (this.state !== 'requires_action') {
                    this.message = ''
                } else if (!this.message) {
                    this.message =
                        "We couldn't confirm that with your bank yet. Try again."
                }
            } catch (error) {
                this.message =
                    error?.message ||
                    "We couldn't confirm that with your bank yet. Try again."
            } finally {
                this.confirming = false
            }
        },
    },
    async mounted() {
        try {
            this.apply(
                await PaymentAuthenticationService.show(
                    this.paymentUlid,
                    this.signature
                )
            )
        } catch (error) {
            this.loadError = this.describeLoadError(error)
        } finally {
            this.loading = false
        }
    },
}
</script>
